import React from 'react'

interface SkeletonProps {
  width?: string | number
  height?: string | number
  borderRadius?: string | number
  className?: string
  style?: React.CSSProperties
}

/**
 * A single skeleton bone — a pulsing placeholder element.
 */
export function Skeleton({ width = '100%', height = 16, borderRadius = 6, className = '', style }: SkeletonProps) {
  return (
    <div
      className={`skeleton-bone ${className}`}
      style={{
        width,
        height,
        borderRadius,
        ...style,
      }}
    />
  )
}

// ── Sidebar conversation list skeleton ──────────────────────────────────────
export function SidebarConversationSkeleton() {
  return (
    <div className="skeleton-sidebar-list">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeleton-sidebar-item">
          <Skeleton height={14} width="75%" borderRadius={6} />
          <Skeleton height={11} width="45%" borderRadius={4} style={{ marginTop: 5 }} />
        </div>
      ))}
    </div>
  )
}

// ── Chat message skeleton ────────────────────────────────────────────────────
export function ChatMessageSkeleton() {
  return (
    <div className="skeleton-chat-message">
      <div className="skeleton-chat-avatar">
        <Skeleton width={28} height={28} borderRadius="50%" />
      </div>
      <div className="skeleton-chat-lines">
        <Skeleton height={14} width="85%" />
        <Skeleton height={14} width="65%" style={{ marginTop: 8 }} />
        <Skeleton height={14} width="75%" style={{ marginTop: 8 }} />
      </div>
    </div>
  )
}

// ── Header skeleton ──────────────────────────────────────────────────────────
export function HeaderSkeleton() {
  return (
    <div className="skeleton-header">
      <Skeleton width={90} height={20} borderRadius={8} />
      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
        <Skeleton width={28} height={28} borderRadius="50%" />
        <Skeleton width={28} height={28} borderRadius="50%" />
      </div>
    </div>
  )
}

// ── Settings panel skeleton ──────────────────────────────────────────────────
export function SettingsPanelSkeleton() {
  return (
    <div className="skeleton-settings">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="skeleton-settings-row">
          <Skeleton height={13} width="40%" borderRadius={5} />
          <Skeleton height={36} width="100%" borderRadius={8} style={{ marginTop: 8 }} />
        </div>
      ))}
    </div>
  )
}

// ── Generic page skeleton ────────────────────────────────────────────────────
export function PageSkeleton() {
  return (
    <div className="skeleton-page">
      <Skeleton height={28} width="50%" borderRadius={8} />
      <Skeleton height={16} width="80%" borderRadius={6} style={{ marginTop: 16 }} />
      <Skeleton height={16} width="70%" borderRadius={6} style={{ marginTop: 8 }} />
      <Skeleton height={16} width="75%" borderRadius={6} style={{ marginTop: 8 }} />
      <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Skeleton height={80} borderRadius={10} />
        <Skeleton height={80} borderRadius={10} />
        <Skeleton height={80} borderRadius={10} />
        <Skeleton height={80} borderRadius={10} />
      </div>
    </div>
  )
}
